import React, { useState, useEffect } from "react";
import axios from "../../../utils/axiosInstance";
import Button from "../../ui/Button";

/**
 * Reusable SubTask picker modal
 * Allows selecting subtasks from the registry
 */
const SubTaskPickerModal = ({
  isOpen,
  onClose,
  onSelect,
  selectionMode = "multiple", // "single" or "multiple"
  existingIds = [] // IDs of already selected subtasks to exclude
}) => {
  const [subTasks, setSubTasks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchSubTasks();
      setSelectedIds([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const fetchSubTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/registry/subtasks", {
        params: { status: "active", limit: 200 }
      });
      setSubTasks(res.data.items || []);
    } catch (error) {
      console.error("Error fetching subtasks:", error);
      alert("Failed to load subtasks from registry");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (subTaskId) => {
    if (selectionMode === "single") {
      setSelectedIds([subTaskId]);
    } else {
      setSelectedIds(prev =>
        prev.includes(subTaskId)
          ? prev.filter(id => id !== subTaskId)
          : [...prev, subTaskId]
      );
    }
  };

  const handleSelect = () => {
    const selected = subTasks.filter(st => selectedIds.includes(st._id));
    onSelect(selected);
    onClose();
  };

  const filteredSubTasks = subTasks.filter(st => {
    // Exclude already selected
    if (existingIds.includes(st._id)) return false;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        st.name?.toLowerCase().includes(term) ||
        st.description?.toLowerCase().includes(term) ||
        st.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    }
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold mb-4">
            Add Subtask{selectionMode === "multiple" ? "s" : ""} from Registry
          </h2>

          {/* Search */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, description, or tags..."
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <p className="text-gray-500">Loading subtasks...</p>
          ) : filteredSubTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {searchTerm
                ? "No subtasks match your search"
                : existingIds.length > 0
                ? "All available subtasks have been added"
                : "No subtasks available in registry"}
            </p>
          ) : (
            <div className="space-y-2">
              {filteredSubTasks.map((subTask) => {
                const isSelected = selectedIds.includes(subTask._id);
                return (
                  <label
                    key={subTask._id}
                    className={`block p-3 border rounded cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type={selectionMode === "single" ? "radio" : "checkbox"}
                        checked={isSelected}
                        onChange={() => toggleSelection(subTask._id)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{subTask.name}</div>
                        {subTask.description && (
                          <div className="text-xs text-gray-600 mt-1">
                            {subTask.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {subTask.weight && `Weight: ${subTask.weight}`}
                          {subTask.weight && subTask.estimatedHours && " • "}
                          {subTask.estimatedHours && `${subTask.estimatedHours}h est.`}
                          {subTask.tags && subTask.tags.length > 0 && (
                            <span className="ml-2">
                              {subTask.tags.map(tag => (
                                <span key={tag} className="inline-block bg-gray-100 rounded px-1 ml-1">
                                  {tag}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedIds.length > 0 && `${selectedIds.length} selected`}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleSelect}
              disabled={selectedIds.length === 0}
            >
              Add {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubTaskPickerModal;
